import { useState, useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { ArrowRight, Loader2, CheckCircle2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger } from "@/components/ui/select";
import { useLang } from "@/i18n";
import { CONSULT } from "@/i18n-consult";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const URL_RE = /^https?:\/\/[^\s]+\.[^\s]{2,}$/i;

const ContactForm = () => {
  const [params] = useSearchParams();
  const { lang } = useLang();
  const c = CONSULT[lang].form;
  const cf = CONSULT[lang].confirm;

  const empty = { fullname: "", email: "", phone: "", company: "", website: "", interest: "", when: "", message: "", budget: "", consent: false, vat: "" };
  const [form, setForm] = useState(() => {
    const base = { ...empty };
    try {
      const area = new URLSearchParams(window.location.search).get("area");
      if (area && CONSULT.it.form.interestOptions.some((i) => i.value === area)) base.interest = area;
    } catch {}
    return base;
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  useEffect(() => {
    const area = params.get("area");
    if (area && c.interestOptions.some((i) => i.value === area)) {
      setForm((f) => ({ ...f, interest: area }));
    }
  }, [params, c.interestOptions, lang]);

  const set = (key) => (e) => {
    const v = e?.target ? (e.target.type === "checkbox" ? e.target.checked : e.target.value) : e;
    setForm((f) => ({ ...f, [key]: v }));
    setErrors((er) => ({ ...er, [key]: undefined }));
  };

  const validate = () => {
    const er = {};
    if (!form.fullname.trim()) er.fullname = "required";
    if (!EMAIL_RE.test(form.email)) er.email = "email";
    if (!form.phone.trim()) er.phone = "required";
    if (!form.company.trim()) er.company = "required";
    if (form.website.trim() && !URL_RE.test(form.website.trim())) er.website = "url";
    if (!form.consent) er.consent = "privacy";
    setErrors(er);
    return Object.keys(er).length === 0;
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    if (form.vat) {
      setSent(true);
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, {
        name: form.fullname,
        company: form.company,
        email: form.email,
        phone: form.phone,
        website: form.website,
        interest: form.interest || "non-so",
        message: form.message || "-",
        start_when: form.when,
        budget: form.budget,
        consent: form.consent,
      });
      setSent(true);
    } catch {
      setErrors({ generic: "generic" });
    } finally {
      setLoading(false);
    }
  };

  const fieldCls =
    "rounded-none border-ink/20 bg-white px-4 py-3 text-base text-ink placeholder:text-mutedink/60 focus-visible:ring-1 focus-visible:ring-accent";
  const err = (k) => errors[k] && <p className="text-xs text-red-700" data-testid={`contact-error-${k}`}>{c.errors[errors[k]]}</p>;
  const labelFor = (opts, v) => opts.find((i) => i.value === v)?.label;

  if (sent) {
    return (
      <div className="border border-ink/12 bg-white p-8 lg:p-12" data-testid="contact-success-panel">
        <CheckCircle2 className="h-10 w-10 text-accent" strokeWidth={1.5} />
        <h3 className="mt-6 font-serif text-3xl font-light text-ink">{cf.title}</h3>
        <p className="mt-4 max-w-lg text-base leading-relaxed text-mutedink">{cf.body}</p>
        <p className="mt-8 font-mono text-[11px] uppercase tracking-[0.25em] text-mutedink">{cf.nextTitle}</p>
        <div className="mt-4 divide-y divide-ink/10 border-y border-ink/10">
          {cf.steps.map((s, i) => (
            <div key={s} className="flex items-baseline gap-4 py-4">
              <span className="font-mono text-xs text-accent">{String(i + 1).padStart(2, "0")}</span>
              <p className="text-sm text-ink">{s}</p>
            </div>
          ))}
        </div>
        <Link
          to="/"
          data-testid="contact-success-home-button"
          className="mt-8 inline-flex items-center gap-3 rounded-md bg-accent px-8 py-4 text-xs font-semibold uppercase tracking-[0.15em] text-white transition-colors duration-300 hover:bg-accent-dark"
        >
          {cf.cta} <ArrowRight className="h-4 w-4" />
        </Link>
      </div>
    );
  }

  return (
    <form onSubmit={submit} noValidate className="border border-ink/12 bg-white p-8 lg:p-10" data-testid="contact-form">
      <p className="font-mono text-xs uppercase tracking-[0.25em] text-accent">{c.overline}</p>
      <h3 className="mt-3 font-serif text-2xl font-light leading-snug text-ink sm:text-3xl">{c.title}</h3>

      <div className="mt-8 grid gap-5 sm:grid-cols-2">
        <div className="space-y-2">
          <Label htmlFor="cf-fullname" className="text-xs uppercase tracking-[0.12em] text-mutedink">{c.fullname}</Label>
          <Input id="cf-fullname" value={form.fullname} onChange={set("fullname")} placeholder={c.phFullname} className={fieldCls} data-testid="contact-input-fullname" />
          {err("fullname")}
        </div>
        <div className="space-y-2">
          <Label htmlFor="cf-email" className="text-xs uppercase tracking-[0.12em] text-mutedink">{c.email}</Label>
          <Input id="cf-email" type="email" value={form.email} onChange={set("email")} placeholder={c.phEmail} className={fieldCls} data-testid="contact-input-email" />
          {err("email")}
        </div>
        <div className="space-y-2">
          <Label htmlFor="cf-phone" className="text-xs uppercase tracking-[0.12em] text-mutedink">{c.phone}</Label>
          <Input id="cf-phone" type="tel" value={form.phone} onChange={set("phone")} placeholder={c.phPhone} className={fieldCls} data-testid="contact-input-phone" />
          {err("phone")}
        </div>
        <div className="space-y-2">
          <Label htmlFor="cf-company" className="text-xs uppercase tracking-[0.12em] text-mutedink">{c.company}</Label>
          <Input id="cf-company" value={form.company} onChange={set("company")} placeholder={c.phCompany} className={fieldCls} data-testid="contact-input-company" />
          {err("company")}
        </div>
        <div className="space-y-2 sm:col-span-2">
          <Label htmlFor="cf-website" className="text-xs uppercase tracking-[0.12em] text-mutedink">{c.website}</Label>
          <Input id="cf-website" type="url" value={form.website} onChange={set("website")} placeholder={c.phWebsite} className={fieldCls} data-testid="contact-input-website" />
          {err("website")}
          <p className="text-xs leading-relaxed text-mutedink/80">{c.websiteNote}</p>
        </div>
        <div className="space-y-2">
          <Label className="text-xs uppercase tracking-[0.12em] text-mutedink">{c.interest}</Label>
          <Select value={form.interest} onValueChange={set("interest")}>
            <SelectTrigger className={`${fieldCls} h-auto`} data-testid="contact-select-interest">
              <span className={form.interest ? "text-ink" : "text-mutedink/60"}>{labelFor(c.interestOptions, form.interest) || "—"}</span>
            </SelectTrigger>
            <SelectContent>
              {c.interestOptions.map((i) => (
                <SelectItem key={i.value} value={i.value} data-testid={`contact-option-${i.value}`}>{i.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label className="text-xs uppercase tracking-[0.12em] text-mutedink">{c.when}</Label>
          <Select value={form.when} onValueChange={set("when")}>
            <SelectTrigger className={`${fieldCls} h-auto`} data-testid="contact-select-when">
              <span className={form.when ? "text-ink" : "text-mutedink/60"}>{labelFor(c.whenOptions, form.when) || "—"}</span>
            </SelectTrigger>
            <SelectContent>
              {c.whenOptions.map((i) => (
                <SelectItem key={i.value} value={i.value} data-testid={`contact-when-${i.value}`}>{i.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2 sm:col-span-2">
          <Label htmlFor="cf-message" className="text-xs uppercase tracking-[0.12em] text-mutedink">{c.message}</Label>
          <Textarea id="cf-message" rows={5} value={form.message} onChange={set("message")} placeholder={c.phMessage} className={`${fieldCls} resize-none`} data-testid="contact-textarea-message" />
        </div>
        <div className="space-y-2 sm:col-span-2">
          <Label className="text-xs uppercase tracking-[0.12em] text-mutedink">{c.budget}</Label>
          <Select value={form.budget} onValueChange={set("budget")}>
            <SelectTrigger className={`${fieldCls} h-auto`} data-testid="contact-select-budget">
              <span className={form.budget ? "text-ink" : "text-mutedink/60"}>{labelFor(c.budgetOptions, form.budget) || "—"}</span>
            </SelectTrigger>
            <SelectContent>
              {c.budgetOptions.map((i) => (
                <SelectItem key={i.value} value={i.value} data-testid={`contact-budget-${i.value}`}>{i.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <p className="text-xs leading-relaxed text-mutedink/80">{c.budgetNote}</p>
        </div>
      </div>

      <input
        type="text"
        name="vat"
        value={form.vat}
        onChange={set("vat")}
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
        className="absolute -left-[9999px] h-0 w-0 opacity-0"
      />

      <div className="mt-6">
        <label className="flex cursor-pointer items-start gap-3 text-sm leading-relaxed text-mutedink">
          <input
            type="checkbox"
            checked={form.consent}
            onChange={set("consent")}
            data-testid="contact-privacy-checkbox"
            className="mt-1 h-4 w-4 shrink-0 cursor-pointer"
            style={{ accentColor: "#2563EB" }}
          />
          <span>
            {c.privacyA}{" "}
            <Link to="/privacy" className="font-medium text-ink underline underline-offset-2 transition-colors hover:text-accent" data-testid="contact-privacy-link">
              {c.privacyLink}
            </Link>{" "}
            {c.privacyB}
          </span>
        </label>
        {err("consent")}
      </div>

      {errors.generic && <p className="mt-4 text-sm text-red-700" data-testid="contact-error-generic">{c.errors.generic}</p>}

      <button
        type="submit"
        disabled={loading}
        data-testid="contact-form-submit-button"
        className="mt-8 inline-flex w-full items-center justify-center gap-3 rounded-md bg-accent px-8 py-4 text-xs font-semibold uppercase tracking-[0.18em] text-white transition-colors duration-300 hover:bg-accent-dark disabled:opacity-60"
      >
        {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArrowRight className="h-4 w-4" />}
        {loading ? c.sending : c.submit}
      </button>
      <p className="mt-4 text-xs leading-relaxed text-mutedink">{c.microcopy}</p>
    </form>
  );
};

export default ContactForm;
